import { useLayoutEffect, useRef, useState } from "react"
import { formatNumberInput, parseNumberInput } from "procurement/design/format/number"

// Thousands-separated number input. `onValue` receives the raw string
// ("1234.5"). Re-inserting commas would push the caret to the end, so it is
// restored to the same digit after each render.
// While an IME (Vietnamese Telex/VNI) is composing, rewriting the value or
// caret makes it re-commit the pending text and duplicate digits, so the DOM
// text is left untouched until composition ends.
export default function useNumberInput(value, onValue) {
  const ref = useRef(null)
  const caretDigits = useRef(null)
  const composing = useRef(false)
  const [composingText, setComposingText] = useState(null)
  const display = composingText ?? formatNumberInput(value)

  useLayoutEffect(() => {
    const el = ref.current
    const target = caretDigits.current
    if (composingText != null) return
    caretDigits.current = null
    if (!el || target == null || document.activeElement !== el) return
    let pos = 0
    for (let seen = 0; pos < display.length && seen < target; pos += 1) {
      if (display[pos] !== ",") seen += 1
    }
    el.setSelectionRange(pos, pos)
  })

  const commit = (e) => {
    const { value: text, selectionStart } = e.target
    caretDigits.current = parseNumberInput(text.slice(0, selectionStart ?? text.length)).length
    onValue?.(parseNumberInput(text))
  }

  const onChange = (e) => {
    if (composing.current) setComposingText(e.target.value)
    commit(e)
  }

  const onCompositionStart = (e) => {
    composing.current = true
    setComposingText(e.target.value)
  }

  const onCompositionEnd = (e) => {
    composing.current = false
    setComposingText(null)
    commit(e)
  }

  return { ref, value: display, onChange, onCompositionStart, onCompositionEnd }
}
